import React, { useState, useRef } from 'react';
import heroImg from '../../../assets/homeowner-portal/referral_hero.webp';
import parentChildImg from '../../../assets/homeowner-portal/referral_dog.webp';
import { Box, Typography, Grid, Card, CardMedia, Stack } from '@mui/material';
import { useQuery, useMutation } from 'react-query';
import { useSnackbar } from 'notistack';
import { HomeownerPortalWrapper } from '../components';
import { HeroSection } from '../components';
import { HubspotFormRenderer } from '../components/hubspot-form/hubspot-form-renderer';
import { SuccessModal } from '../components/hubspot-form/success-modal';
import { getHubspotReferralForm, submitHubspotReferralForm } from '../../../fetch';
import { IHubspotForm, HubspotFormField } from '../../../models';

export const ReferralProgram: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const formikRef = useRef<any>(null);

  const {
    data: form,
    isLoading,
    isError,
    error
  } = useQuery<IHubspotForm>(['hubspotForm', 'referral'], getHubspotReferralForm);

  const submitMutation = useMutation(
    (values: HubspotFormField[]) => submitHubspotReferralForm(values),
    {
      onSuccess: () => {
        setShowSuccessModal(true);
      },
      onError: () => {
        enqueueSnackbar('Error submitting form. Please try again.', {
          variant: 'error'
        });
      }
    }
  );

  const handleSubmit = (values: Record<string, any>) => {
    if (!form) return;

    // Hidden fields that should be auto-populated (temporary fix for API bug)
    const hiddenFieldDefaults: Record<string, string> = {
      lead_source_master: 'Web Lead',
      lead_attribution: 'Form - HP Referral Form',
      route_to_osc: 'Yes, route.'
    };

    // Transform values into array of { objectTypeId, name, value }
    const formattedFields: HubspotFormField[] = [];
    form.fieldGroups.forEach(group => {
      group.fields.forEach(field => {
        // Use default value for hidden fields, otherwise use form value
        const fieldValue = hiddenFieldDefaults[field.name] || values[field.name];

        formattedFields.push({
          objectTypeId: field.objectTypeId,
          name: field.name,
          value: fieldValue
        });
      });
    });
    submitMutation.mutate(formattedFields);
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);

    if (formikRef.current) {
      formikRef.current.resetForm();
    }
  };

  return (
    <HomeownerPortalWrapper>
      <Box sx={{ minHeight: '100vh' }}>
        {/* Hero Section */}
        <HeroSection
          image={heroImg}
          title="Share the Joy of Homeownership"
          subtitle="Your referral means the world to us!"
          backgroundPosition="0 35%"
        />

        {/* Program Info Section */}
        <Stack gap={2} mt={4}>
          <Box
            sx={{
              backgroundColor: 'backgroundBlue.main',
              padding: { xs: 4, md: 0 },
              paddingLeft: { xs: 4, md: 6 },
              paddingRight: { xs: 4, md: 6 }
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography
                  variant="h2"
                  fontWeight={700}
                  sx={{ mb: 2, color: theme => theme.palette.primary.dark }}
                >
                  Stone Martin Builders Referral Program
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mb: 2, color: theme => theme.palette.primary.dark }}
                >
                  At Stone Martin Builders, we truly believe there's no better compliment than a
                  referral from one of our homeowners.
                </Typography>
                <Typography variant="body1" sx={{ color: theme => theme.palette.primary.dark }}>
                  If you know a friend, family member, or co-worker who's thinking about building or
                  buying a new home, we'd love for you to send them our way. As a thank you, our
                  Homeowner Referral Program offers a ------- to show our appreciation for sharing
                  the Stone Martin experience.
                </Typography>
              </Grid>
              <Grid item xs={12} md={5} sx={{ position: 'relative' }}>
                <Card sx={{ boxShadow: 0, pointerEvents: 'none', cursor: 'default' }}>
                  <CardMedia
                    component="img"
                    image={parentChildImg}
                    alt="Parent holding child in field"
                  />
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Referral Form Section */}
          <Box pl={4} pr={4} pb={4}>
            {isLoading && (
              <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                <Typography variant="body1">Loading form...</Typography>
              </Box>
            )}
            {isError && (
              <Typography color="error">Failed to load form: {(error as Error).message}</Typography>
            )}
            {form && (
              <HubspotFormRenderer
                formName="Referral Form"
                form={form}
                onSubmit={handleSubmit}
                isSubmitting={submitMutation.isLoading}
                submitButtonText="Submit Referral"
                formRef={formikRef}
                hiddenFields={['lead_source_master', 'lead_attribution', 'route_to_osc']}
              />
            )}
          </Box>
        </Stack>
      </Box>

      <SuccessModal
        open={showSuccessModal}
        onClose={handleSuccessModalClose}
        title="Thanks for Spreading the Word!"
        content={
          <>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
              We're grateful you chose to share the Stone Martin experience with someone you care
              about. Your referral helps us grow our community with more amazing homeowners just
              like you. We'll be in touch soon with next steps - and to show our appreciation for
              your trust in us.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
              Here's to building something special, together.
            </Typography>
          </>
        }
      />
    </HomeownerPortalWrapper>
  );
};
